// src/admin/components/BranchDrawer.tsx
import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Drawer,
    TextField,
    Typography,
    IconButton,
    Alert,
    CircularProgress,
    Grid,
    FormControlLabel,
    Switch,
    Divider,
    Card,
    CardContent,
    Stepper,
    Step,
    StepLabel,
    StepContent,
    InputAdornment,
    Backdrop,
    LinearProgress,
    MenuItem,
} from '@mui/material';
import {
    Close as CloseIcon,
    LocationOn as LocationIcon,
    Phone as PhoneIcon,
    Email as EmailIcon,
    Person as PersonIcon,
    Schedule as ScheduleIcon,
} from '@mui/icons-material';
import {
    Warehouse,
    WarehouseAddress,
    WarehouseContact,
    WarehouseFormData,
    DayHours,
    DayOfWeek,
    DAYS_OF_WEEK,
    DAY_LABELS,
    createDefaultOperatingHours,
} from './branch';
import { WarehouseService } from './branchService';
import { useAuth } from '../../../contexts/AuthContext';
import { countries, getCitiesByCountryName } from '../../../enums/countries';
import DrawerFooter from '../../components/DrawerFooter';

// Formats a "HH:mm" 24h time string as a friendly 12h label, e.g. "09:00" -> "9:00 AM".
// Falls back to the raw value if it doesn't look like a valid time.
const formatTime12h = (time: string): string => {
    const match = /^(\d{1,2}):(\d{2})$/.exec(time || '');
    if (!match) return time || '—';
    const hour = parseInt(match[1], 10);
    const minute = match[2];
    const period = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 === 0 ? 12 : hour % 12;
    return `${displayHour}:${minute} ${period}`;
};

interface WarehouseDrawerProps {
    isOpen: boolean;
    mode: 'add' | 'edit' | 'view';
    warehouse: Warehouse | null;
    onClose: () => void;
    onSave: () => void;
    onShowToast: (message: string, severity: 'success' | 'error' | 'warning' | 'info') => void;
}

const WarehouseDrawer: React.FC<WarehouseDrawerProps> = ({
    isOpen,
    mode,
    warehouse,
    onClose,
    onSave,
    onShowToast,
}) => {
    const { currentUser } = useAuth();
    const [activeStep, setActiveStep] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const [formData, setFormData] = useState<WarehouseFormData>({
        name: '',
        code: '',
        description: '',
        address: {
            street: '',
            city: '',
            state: '',
            country: '',
            zipCode: '',
        },
        contact: {
            phone: '',
            email: '',
            manager: '',
        },
        capacity: {
            total: '',
            used: '0',
        },
        isActive: true,
        operatingHours: createDefaultOperatingHours(),
        displayOnWebsite: false,
    });

    useEffect(() => {
        if (isOpen && warehouse && (mode === 'edit' || mode === 'view')) {
            const newFormData = {
                name: warehouse.name,
                code: warehouse.code,
                description: warehouse.description,
                address: warehouse.address,
                contact: warehouse.contact,
                capacity: {
                    total: warehouse.capacity.total.toString(),
                    used: warehouse.capacity.used.toString(),
                },
                isActive: warehouse.isActive,
                // `warehouse.operatingHours` may be undefined for docs that
                // predate per-day hours (or that only ever had the old flat
                // shape, which branchService already normalizes away to
                // undefined) — fall back to sensible defaults either way.
                operatingHours: warehouse.operatingHours || createDefaultOperatingHours(),
                displayOnWebsite: warehouse.displayOnWebsite || false,
            };

            setFormData(newFormData);
        } else if (isOpen && mode === 'add') {
            resetForm();
        }
        setError('');
        setActiveStep(0);
    }, [isOpen, warehouse, mode]);

    const resetForm = () => {
        setFormData({
            name: '',
            code: '',
            description: '',
            address: {
                street: '',
                city: '',
                state: '',
                country: '',
                zipCode: '',
            },
            contact: {
                phone: '',
                email: '',
                manager: '',
            },
            capacity: {
                total: '',
                used: '0',
            },
            isActive: true,
            operatingHours: createDefaultOperatingHours(),
            displayOnWebsite: false,
        });
    };

    // Fixed handleInputChange function
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value, type } = e.target;

        if (name === 'address.country') {
            setFormData(prev => ({
                ...prev,
                address: {
                    ...prev.address,
                    country: value,
                    city: ''
                }
            }));
        } else if (name.startsWith('address.')) {
            const field = name.replace('address.', '') as keyof WarehouseAddress;
            setFormData(prev => ({
                ...prev,
                address: {
                    ...prev.address,
                    [field]: value
                }
            }));
        } else if (name.startsWith('contact.')) {
            const field = name.replace('contact.', '') as keyof WarehouseContact;
            setFormData(prev => ({
                ...prev,
                contact: {
                    ...prev.contact,
                    [field]: value
                }
            }));
        } else if (name.startsWith('capacity.')) {
            const field = name.replace('capacity.', '') as 'total' | 'used';
            setFormData(prev => ({
                ...prev,
                capacity: {
                    ...prev.capacity,
                    [field]: value
                }
            }));
        } else if (name === 'operatingHours.timezone') {
            setFormData(prev => ({
                ...prev,
                operatingHours: {
                    ...prev.operatingHours,
                    timezone: value,
                }
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
            }));
        }
    };

    // Updates a single day's open/close/closed value within operatingHours.
    const handleDayHoursChange = (day: DayOfWeek, field: keyof DayHours, value: string | boolean) => {
        setFormData(prev => ({
            ...prev,
            operatingHours: {
                ...prev.operatingHours,
                [day]: {
                    ...prev.operatingHours[day],
                    [field]: value,
                },
            },
        }));
    };

    const validateForm = (): boolean => {
        if (!formData.name.trim()) {
            setError('Show Room name is required');
            onShowToast('Show Room name is required', 'error');
            return false;
        }

        if (!formData.code.trim()) {
            setError('Show Room code is required');
            onShowToast('Show Room code is required', 'error');
            return false;
        }

        if (formData.code.length < 2 || formData.code.length > 10) {
            setError('Show Room code must be between 2 and 10 characters');
            onShowToast('Show Room code must be between 2 and 10 characters', 'error');
            return false;
        }

        if (!formData.address.street.trim()) {
            setError('Street address is required');
            onShowToast('Street address is required', 'error');
            return false;
        }

        if (!formData.address.city.trim()) {
            setError('City is required');
            onShowToast('City is required', 'error');
            return false;
        }

        if (!formData.address.country.trim()) {
            setError('Country is required');
            onShowToast('Country is required', 'error');
            return false;
        }

        if (!formData.address.state.trim()) {
            setError('State/Province is required');
            onShowToast('State/Province is required', 'error');
            return false;
        }

        if (!formData.contact.manager.trim()) {
            setError('Manager name is required');
            onShowToast('Manager name is required', 'error');
            return false;
        }

        if (!formData.capacity.total || parseFloat(formData.capacity.total) <= 0) {
            setError('Total capacity must be greater than 0');
            onShowToast('Total capacity must be greater than 0', 'error');
            return false;
        }

        const used = parseFloat(formData.capacity.used) || 0;
        const total = parseFloat(formData.capacity.total) || 0;
        if (used > total) {
            setError('Used capacity cannot exceed total capacity');
            onShowToast('Used capacity cannot exceed total capacity', 'error');
            return false;
        }

        return true;
    };

    const getUserName = () => {
        if (!currentUser) return 'Unknown User';

        // Adjust these based on your user object structure
        return currentUser.displayName || currentUser.email ||
            'Unknown User';
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (mode === 'view') return;

        if (!validateForm()) return;

        try {
            setLoading(true);
            setError('');

            const userId = currentUser?.uid || 'unknown';
            const userName = getUserName();

            if (mode === 'add') {
                await WarehouseService.createWarehouse(formData, userId, userName);
                onShowToast('Show Room created successfully', 'success');
            } else if (mode === 'edit' && warehouse?.id) {
                await WarehouseService.updateWarehouse(warehouse.id, formData, userId, userName);
                onShowToast('Show Room updated successfully', 'success');
            }

            onSave();
        } catch (err: any) {
            console.error('Error saving warehouse:', err);
            setError(err.message || 'Failed to save show room');
            onShowToast(err.message || 'Failed to save show room', 'error');
        } finally {
            setLoading(false);
        }
    };

    const getTitle = () => {
        switch (mode) {
            case 'add':
                return 'Add New Show Room';
            case 'edit':
                return 'Edit Show Room';
            case 'view':
                return 'Show Room Details';
            default:
                return '';
        }
    };

    const steps = ['Basic Information', 'Address & Contact', 'Capacity & Hours'];

    if (!isOpen) return null;

    return (
        <>
            <Drawer
                anchor="right"
                open={isOpen}
                onClose={onClose}
                PaperProps={{
                    sx: {
                        width: { xs: '100%', sm: '100%', md: 700 },
                        maxWidth: 700
                    }
                }}
            >
                <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    {/* Header */}
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        p: 2,
                        borderBottom: 1,
                        borderColor: 'divider'
                    }}>
                        <Typography variant="h6">{getTitle()}</Typography>
                        <IconButton onClick={onClose} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    {/* Content */}
                    <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
                        <Box component="form" onSubmit={handleSubmit} sx={{ p: 3 }}>
                            {error && (
                                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
                                    {error}
                                </Alert>
                            )}

                            {mode !== 'view' ? (
                                <Stepper activeStep={activeStep} orientation="vertical" sx={{ mb: 3 }}>
                                    {/* Step 1: Basic Information */}
                                    <Step>
                                        <StepLabel>Basic Information</StepLabel>
                                        <StepContent>
                                            <Grid container spacing={2}>
                                                <Grid item xs={12}>
                                                    <TextField
                                                        fullWidth
                                                        label="Show Room Name *"
                                                        name="name"
                                                        value={formData.name}
                                                        onChange={handleInputChange}
                                                        placeholder="Enter show room name"
                                                    />
                                                </Grid>

                                                <Grid item xs={12} sm={6}>
                                                    <TextField
                                                        fullWidth
                                                        label="Show Room Code *"
                                                        name="code"
                                                        value={formData.code}
                                                        onChange={handleInputChange}
                                                        placeholder="e.g., WH-001"
                                                        inputProps={{
                                                            style: { textTransform: 'uppercase' },
                                                            maxLength: 10
                                                        }}
                                                    />
                                                </Grid>

                                                <Grid item xs={12} sm={6}>
                                                    <FormControlLabel
                                                        control={
                                                            <Switch
                                                                name="isActive"
                                                                checked={formData.isActive}
                                                                onChange={handleInputChange}
                                                                color="primary"
                                                            />
                                                        }
                                                        label="Active Show Room"
                                                    />
                                                </Grid>

                                                <Grid item xs={12} sm={6}>
                                                    <FormControlLabel
                                                        control={
                                                            <Switch
                                                                name="displayOnWebsite"
                                                                checked={formData.displayOnWebsite}
                                                                onChange={handleInputChange}
                                                                color="primary"
                                                            />
                                                        }
                                                        label="Display in Website"
                                                    />
                                                </Grid>

                                                <Grid item xs={12}>
                                                    <TextField
                                                        fullWidth
                                                        label="Description"
                                                        name="description"
                                                        value={formData.description}
                                                        onChange={handleInputChange}
                                                        multiline
                                                        rows={3}
                                                        placeholder="Enter show room description"
                                                    />
                                                </Grid>
                                            </Grid>
                                        </StepContent>
                                    </Step>

                                    {/* Step 2: Address & Contact */}
                                    <Step>
                                        <StepLabel>Address & Contact</StepLabel>
                                        <StepContent>
                                            <Card variant="outlined" sx={{ mb: 2 }}>
                                                <CardContent>
                                                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                                                        <LocationIcon sx={{ mr: 1 }} />
                                                        Address Information
                                                    </Typography>
                                                    <Grid container spacing={2}>
                                                        <Grid item xs={12}>
                                                            <TextField
                                                                fullWidth
                                                                label="Street Address *"
                                                                name="address.street"
                                                                value={formData.address.street}
                                                                onChange={handleInputChange}
                                                                placeholder="Enter street address"
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} sm={6}>
                                                            <TextField
                                                                fullWidth
                                                                select
                                                                label="Country *"
                                                                name="address.country"
                                                                value={formData.address.country}
                                                                onChange={handleInputChange}
                                                                placeholder="Select country"
                                                            >
                                                                {formData.address.country && !countries.some(c => c.name === formData.address.country) && (
                                                                    <MenuItem value={formData.address.country}>{formData.address.country}</MenuItem>
                                                                )}
                                                                {countries.map(c => (
                                                                    <MenuItem key={c.code} value={c.name}>{c.name}</MenuItem>
                                                                ))}
                                                            </TextField>
                                                        </Grid>
                                                        <Grid item xs={12} sm={6}>
                                                            <TextField
                                                                fullWidth
                                                                select
                                                                label="City *"
                                                                name="address.city"
                                                                value={formData.address.city}
                                                                onChange={handleInputChange}
                                                                disabled={!formData.address.country}
                                                                placeholder="Select city"
                                                            >
                                                                {(() => {
                                                                    const cities = getCitiesByCountryName(formData.address.country);
                                                                    const options = formData.address.city && !cities.includes(formData.address.city)
                                                                        ? [formData.address.city, ...cities]
                                                                        : cities;
                                                                    return options.map(city => (
                                                                        <MenuItem key={city} value={city}>{city}</MenuItem>
                                                                    ));
                                                                })()}
                                                            </TextField>
                                                        </Grid>
                                                        <Grid item xs={12} sm={6}>
                                                            <TextField
                                                                fullWidth
                                                                label="State/Province *"
                                                                name="address.state"
                                                                value={formData.address.state}
                                                                onChange={handleInputChange}
                                                                placeholder="Enter state or province"
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} sm={6}>
                                                            <TextField
                                                                fullWidth
                                                                label="ZIP/Postal Code"
                                                                name="address.zipCode"
                                                                value={formData.address.zipCode}
                                                                onChange={handleInputChange}
                                                                placeholder="Enter ZIP code"
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                </CardContent>
                                            </Card>

                                            <Card variant="outlined">
                                                <CardContent>
                                                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                                                        <PersonIcon sx={{ mr: 1 }} />
                                                        Contact Information
                                                    </Typography>
                                                    <Grid container spacing={2}>
                                                        <Grid item xs={12} sm={6}>
                                                            <TextField
                                                                fullWidth
                                                                label="Manager Name *"
                                                                name="contact.manager"
                                                                value={formData.contact.manager}
                                                                onChange={handleInputChange}
                                                                placeholder="Enter manager name"
                                                                InputProps={{
                                                                    startAdornment: (
                                                                        <InputAdornment position="start">
                                                                            <PersonIcon />
                                                                        </InputAdornment>
                                                                    ),
                                                                }}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} sm={6}>
                                                            <TextField
                                                                fullWidth
                                                                label="Phone Number"
                                                                name="contact.phone"
                                                                value={formData.contact.phone}
                                                                onChange={handleInputChange}
                                                                placeholder="Enter phone number"
                                                                InputProps={{
                                                                    startAdornment: (
                                                                        <InputAdornment position="start">
                                                                            <PhoneIcon />
                                                                        </InputAdornment>
                                                                    ),
                                                                }}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12}>
                                                            <TextField
                                                                fullWidth
                                                                label="Email Address"
                                                                name="contact.email"
                                                                type="email"
                                                                value={formData.contact.email}
                                                                onChange={handleInputChange}
                                                                placeholder="Enter email address"
                                                                InputProps={{
                                                                    startAdornment: (
                                                                        <InputAdornment position="start">
                                                                            <EmailIcon />
                                                                        </InputAdornment>
                                                                    ),
                                                                }}
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                </CardContent>
                                            </Card>
                                        </StepContent>
                                    </Step>

                                    {/* Step 3: Capacity & Hours */}
                                    <Step>
                                        <StepLabel>Capacity & Hours</StepLabel>
                                        <StepContent>
                                            <Card variant="outlined" sx={{ mb: 2 }}>
                                                <CardContent>
                                                    <Typography variant="h6" gutterBottom>
                                                        Capacity Information
                                                    </Typography>
                                                    <Grid container spacing={2}>
                                                        <Grid item xs={12} sm={6}>
                                                            <TextField
                                                                fullWidth
                                                                label="Total Capacity *"
                                                                name="capacity.total"
                                                                type="number"
                                                                value={formData.capacity.total}
                                                                onChange={handleInputChange}
                                                                placeholder="0"
                                                                inputProps={{ min: 0, step: "0.01" }}
                                                                helperText="Total storage capacity"
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} sm={6}>
                                                            <TextField
                                                                fullWidth
                                                                disabled
                                                                label="Used Capacity"
                                                                name="capacity.used"
                                                                type="number"
                                                                value={formData.capacity.used}
                                                                placeholder="0"
                                                                inputProps={{ min: 0, step: "0.01" }}
                                                                helperText="Calculated automatically from vehicles currently at this show room"
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12}>
                                                            <Box sx={{ p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                                                                <Typography variant="body2" color="text.secondary">
                                                                    Available Capacity: {(
                                                                        parseFloat(formData.capacity.total || '0') -
                                                                        parseFloat(formData.capacity.used || '0')
                                                                    ).toLocaleString()} units
                                                                </Typography>
                                                            </Box>
                                                        </Grid>
                                                    </Grid>
                                                </CardContent>
                                            </Card>

                                            <Card variant="outlined">
                                                <CardContent>
                                                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                                                        <ScheduleIcon sx={{ mr: 1 }} />
                                                        Operating Hours
                                                    </Typography>

                                                    <Grid container spacing={2} sx={{ mb: 2 }}>
                                                        <Grid item xs={12} sm={6}>
                                                            <TextField
                                                                fullWidth
                                                                label="Timezone"
                                                                name="operatingHours.timezone"
                                                                value={formData.operatingHours.timezone}
                                                                onChange={handleInputChange}
                                                                placeholder="e.g., UTC"
                                                                helperText="Applies to all days below"
                                                            />
                                                        </Grid>
                                                    </Grid>

                                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                                        {DAYS_OF_WEEK.map((day) => {
                                                            const dayHours = formData.operatingHours[day];
                                                            return (
                                                                <Box
                                                                    key={day}
                                                                    sx={{
                                                                        display: 'flex',
                                                                        flexWrap: 'wrap',
                                                                        alignItems: 'center',
                                                                        gap: 2,
                                                                        p: 1,
                                                                        borderRadius: 1,
                                                                        bgcolor: 'grey.50',
                                                                    }}
                                                                >
                                                                    <Typography variant="body2" sx={{ width: 96, fontWeight: 500, flexShrink: 0 }}>
                                                                        {DAY_LABELS[day]}
                                                                    </Typography>

                                                                    <FormControlLabel
                                                                        sx={{ mr: 0 }}
                                                                        control={
                                                                            <Switch
                                                                                size="small"
                                                                                checked={!dayHours.closed}
                                                                                onChange={(e) => handleDayHoursChange(day, 'closed', !e.target.checked)}
                                                                                color="primary"
                                                                            />
                                                                        }
                                                                        label={dayHours.closed ? 'Closed' : 'Open'}
                                                                    />

                                                                    <TextField
                                                                        label="Open"
                                                                        type="time"
                                                                        size="small"
                                                                        disabled={dayHours.closed}
                                                                        value={dayHours.open}
                                                                        onChange={(e) => handleDayHoursChange(day, 'open', e.target.value)}
                                                                        sx={{ width: 140 }}
                                                                        InputLabelProps={{ shrink: true }}
                                                                    />

                                                                    <TextField
                                                                        label="Close"
                                                                        type="time"
                                                                        size="small"
                                                                        disabled={dayHours.closed}
                                                                        value={dayHours.close}
                                                                        onChange={(e) => handleDayHoursChange(day, 'close', e.target.value)}
                                                                        sx={{ width: 140 }}
                                                                        InputLabelProps={{ shrink: true }}
                                                                    />
                                                                </Box>
                                                            );
                                                        })}
                                                    </Box>
                                                </CardContent>
                                            </Card>
                                        </StepContent>
                                    </Step>
                                </Stepper>
                            ) : (
                                // View Mode
                                <Box>
                                    {warehouse && (
                                        <Grid container spacing={3}>
                                            {/* Basic Information */}
                                            <Grid item xs={12}>
                                                <Card>
                                                    <CardContent>
                                                        <Typography variant="h6" gutterBottom>
                                                            Basic Information
                                                        </Typography>
                                                        <Grid container spacing={2}>
                                                            <Grid item xs={12} sm={6}>
                                                                <Typography variant="body2" color="text.secondary">
                                                                    Name
                                                                </Typography>
                                                                <Typography variant="body1" gutterBottom>
                                                                    {warehouse.name}
                                                                </Typography>
                                                            </Grid>
                                                            <Grid item xs={12} sm={6}>
                                                                <Typography variant="body2" color="text.secondary">
                                                                    Code
                                                                </Typography>
                                                                <Typography variant="body1" gutterBottom>
                                                                    {warehouse.code}
                                                                </Typography>
                                                            </Grid>
                                                            <Grid item xs={12}>
                                                                <Typography variant="body2" color="text.secondary">
                                                                    Description
                                                                </Typography>
                                                                <Typography variant="body1" gutterBottom>
                                                                    {warehouse.description || 'No description provided'}
                                                                </Typography>
                                                            </Grid>
                                                            <Grid item xs={12} sm={6}>
                                                                <Typography variant="body2" color="text.secondary">
                                                                    Status
                                                                </Typography>
                                                                <Typography
                                                                    variant="body1"
                                                                    color={warehouse.isActive ? 'success.main' : 'error.main'}
                                                                    sx={{ fontWeight: 600 }}
                                                                >
                                                                    {warehouse.isActive ? 'Active' : 'Inactive'}
                                                                </Typography>
                                                            </Grid>
                                                            <Grid item xs={12} sm={6}>
                                                                <Typography variant="body2" color="text.secondary">
                                                                    Display in Website
                                                                </Typography>
                                                                <Typography
                                                                    variant="body1"
                                                                    color={warehouse.displayOnWebsite ? 'success.main' : 'text.secondary'}
                                                                    sx={{ fontWeight: 600 }}
                                                                >
                                                                    {warehouse.displayOnWebsite ? 'Visible on website' : 'Hidden from website'}
                                                                </Typography>
                                                            </Grid>
                                                        </Grid>
                                                    </CardContent>
                                                </Card>
                                            </Grid>

                                            {/* Address Information */}
                                            <Grid item xs={12} md={6}>
                                                <Card>
                                                    <CardContent>
                                                        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                                                            <LocationIcon sx={{ mr: 1 }} />
                                                            Address Information
                                                        </Typography>
                                                        <Box sx={{ mt: 2 }}>
                                                            <Typography variant="body1" paragraph sx={{ fontWeight: 500 }}>
                                                                {warehouse.address.street}
                                                            </Typography>
                                                            <Typography variant="body1" paragraph>
                                                                {warehouse.address.city}, {warehouse.address.state} {warehouse.address.zipCode}
                                                            </Typography>
                                                            <Typography variant="body1">
                                                                {warehouse.address.country}
                                                            </Typography>
                                                        </Box>
                                                    </CardContent>
                                                </Card>
                                            </Grid>

                                            {/* Contact Information */}
                                            <Grid item xs={12} md={6}>
                                                <Card>
                                                    <CardContent>
                                                        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                                                            <PersonIcon sx={{ mr: 1 }} />
                                                            Contact Information
                                                        </Typography>
                                                        <Box sx={{ mt: 2 }}>
                                                            <Typography variant="body1" paragraph>
                                                                <strong>Manager:</strong> {warehouse.contact.manager}
                                                            </Typography>
                                                            <Typography variant="body1" paragraph>
                                                                <strong>Phone:</strong> {warehouse.contact.phone || 'Not provided'}
                                                            </Typography>
                                                            <Typography variant="body1">
                                                                <strong>Email:</strong> {warehouse.contact.email || 'Not provided'}
                                                            </Typography>
                                                        </Box>
                                                    </CardContent>
                                                </Card>
                                            </Grid>

                                            {/* Capacity Information */}
                                            <Grid item xs={12} md={6}>
                                                <Card>
                                                    <CardContent>
                                                        <Typography variant="h6" gutterBottom>
                                                            Capacity Information
                                                        </Typography>
                                                        <Box sx={{ mt: 2 }}>
                                                            <Box sx={{ mb: 3 }}>
                                                                <Typography variant="body2" color="text.secondary">
                                                                    Total Capacity
                                                                </Typography>
                                                                <Typography variant="h6" color="primary">
                                                                    {warehouse.capacity.total.toLocaleString()} units
                                                                </Typography>
                                                            </Box>
                                                            <Box sx={{ mb: 3 }}>
                                                                <Typography variant="body2" color="text.secondary">
                                                                    Used Capacity
                                                                </Typography>
                                                                <Typography variant="h6" color="warning.main">
                                                                    {warehouse.capacity.used.toLocaleString()} units
                                                                </Typography>
                                                            </Box>
                                                            <Box>
                                                                <Typography variant="body2" color="text.secondary">
                                                                    Available Capacity
                                                                </Typography>
                                                                <Typography variant="h6" color="success.main">
                                                                    {warehouse.capacity.available.toLocaleString()} units
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                    </CardContent>
                                                </Card>
                                            </Grid>

                                            {/* Operating Hours */}
                                            <Grid item xs={12} md={6}>
                                                <Card>
                                                    <CardContent>
                                                        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                                                            <ScheduleIcon sx={{ mr: 1 }} />
                                                            Operating Hours
                                                        </Typography>
                                                        {warehouse.operatingHours ? (
                                                            <Box sx={{ mt: 1 }}>
                                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                                                    Timezone: {warehouse.operatingHours.timezone || 'UTC'}
                                                                </Typography>
                                                                {DAYS_OF_WEEK.map((day) => {
                                                                    const dayHours = warehouse.operatingHours?.[day];
                                                                    return (
                                                                        <Box
                                                                            key={day}
                                                                            sx={{
                                                                                display: 'flex',
                                                                                justifyContent: 'space-between',
                                                                                py: 0.5,
                                                                                borderBottom: 1,
                                                                                borderColor: 'divider',
                                                                                '&:last-of-type': { borderBottom: 0 },
                                                                            }}
                                                                        >
                                                                            <Typography variant="body2">{DAY_LABELS[day]}</Typography>
                                                                            <Typography
                                                                                variant="body2"
                                                                                color={dayHours && !dayHours.closed ? 'text.primary' : 'text.secondary'}
                                                                            >
                                                                                {dayHours
                                                                                    ? (dayHours.closed
                                                                                        ? 'Closed'
                                                                                        : `${formatTime12h(dayHours.open)} – ${formatTime12h(dayHours.close)}`)
                                                                                    : 'Not set'}
                                                                            </Typography>
                                                                        </Box>
                                                                    );
                                                                })}
                                                            </Box>
                                                        ) : (
                                                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                                                Operating hours have not been set for this show room.
                                                            </Typography>
                                                        )}
                                                    </CardContent>
                                                </Card>
                                            </Grid>

                                            {/* Additional Information */}
                                            <Grid item xs={12}>
                                                <Card>
                                                    <CardContent>
                                                        <Typography variant="h6" gutterBottom>
                                                            Additional Information
                                                        </Typography>
                                                        <Grid container spacing={2}>
                                                            <Grid item xs={12} sm={6}>
                                                                <Typography variant="body2" color="text.secondary">
                                                                    Created Date
                                                                </Typography>
                                                                <Typography variant="body1">
                                                                    {warehouse.createdAt ? new Date(warehouse.createdAt).toLocaleDateString() : 'Unknown'}
                                                                </Typography>
                                                            </Grid>
                                                            <Grid item xs={12} sm={6}>
                                                                <Typography variant="body2" color="text.secondary">
                                                                    Last Updated
                                                                </Typography>
                                                                <Typography variant="body1">
                                                                    {warehouse.updatedAt ? new Date(warehouse.updatedAt).toLocaleDateString() : 'Unknown'}
                                                                </Typography>
                                                            </Grid>
                                                            {warehouse.createdByName && (
                                                                <Grid item xs={12} sm={6}>
                                                                    <Typography variant="body2" color="text.secondary">
                                                                        Created By
                                                                    </Typography>
                                                                    <Typography variant="body1">
                                                                        {warehouse.createdByName}
                                                                    </Typography>
                                                                </Grid>
                                                            )}
                                                        </Grid>
                                                    </CardContent>
                                                </Card>
                                            </Grid>
                                        </Grid>
                                    )}
                                </Box>
                            )}
                        </Box>
                    </Box>

                    {mode !== 'view' && (
                        <DrawerFooter>
                            {activeStep > 0 && (
                                <Button onClick={() => setActiveStep(activeStep - 1)} variant="outlined" disabled={loading}>
                                    Back
                                </Button>
                            )}
                            {activeStep < 2 ? (
                                <Button
                                    onClick={() => setActiveStep(activeStep + 1)}
                                    variant="contained"
                                    disabled={
                                        activeStep === 0
                                            ? (!formData.name.trim() || !formData.code.trim())
                                            : (!formData.address.street.trim() || !formData.address.city.trim() || !formData.address.country || !formData.address.state || !formData.contact.manager.trim())
                                    }
                                >
                                    Continue
                                </Button>
                            ) : (
                                <Button
                                    onClick={handleSubmit}
                                    variant="contained"
                                    disabled={loading || !formData.capacity.total}
                                    startIcon={loading && <CircularProgress size={16} />}
                                >
                                    {mode === 'add' ? 'Create Show Room' : 'Update Show Room'}
                                </Button>
                            )}
                        </DrawerFooter>
                    )}
                </Box>
            </Drawer>

            {/* ── Full-screen saving overlay (mirrors EnhancedProductDrawer pattern) ── */}
            <Backdrop
                open={loading}
                sx={{ zIndex: (t) => t.zIndex.drawer + 200, flexDirection: 'column', gap: 3, bgcolor: 'rgba(0,0,0,0.6)' }}
            >
                <Box
                    sx={{
                        bgcolor: 'background.paper',
                        borderRadius: 3,
                        px: 5,
                        py: 4,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 2.5,
                        minWidth: 280,
                        boxShadow: 24,
                    }}
                >
                    <CircularProgress size={48} thickness={4} />
                    <Box sx={{ width: '100%' }}>
                        <LinearProgress sx={{ borderRadius: 1, mb: 1.5 }} />
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, textAlign: 'center', color: 'text.primary' }}>
                            {mode === 'add' ? 'Creating Show Room…' : 'Updating Show Room…'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 0.5 }}>
                            Please wait, do not close this window
                        </Typography>
                    </Box>
                </Box>
            </Backdrop>
        </>
    );
};

export default WarehouseDrawer;